import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Menu, User as UserIcon, Building2, Shield } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../ui/dropdown-menu";
import { useAuth } from "../../lib/auth-context";
import { ROLE_LABELS } from "../../lib/labels";
import { toast } from "sonner";
import ThemeToggle from "../theme/ThemeToggle";

export default function Topbar({ onMobileMenu }) {
  const { user, tenant, role, tenants, isSuperAdmin, logout, switchTenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initials = user?.full_name
    ? user.full_name
        .split(" ")
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "?";

  const isSystemRoute = Boolean(isSuperAdmin && location?.pathname?.startsWith("/admin/"));
  const tenantLabel = isSuperAdmin
    ? (isSystemRoute || !tenant?.name ? "Systemansicht" : `Aktiver Mandant: ${tenant.name}`)
    : (tenant?.name || "—");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleSwitch = async (slug) => {
    if (slug === tenant?.slug) return;
    const r = await switchTenant(slug);
    if (r.ok) {
      toast.success("Mandant gewechselt.");
      window.location.reload();
    } else {
      toast.error(r.error || "Wechsel fehlgeschlagen.");
    }
  };

  return (
    <header className="sticky top-0 z-30 min-h-16 bg-background/80 backdrop-blur-xl border-b border-border flex flex-wrap items-center gap-3 px-3 py-2 md:h-16 md:flex-nowrap md:px-6 md:py-0">
      <button
        type="button"
        onClick={onMobileMenu}
        className="order-1 md:hidden p-2 -ml-2 rounded-lg hover:bg-muted"
        aria-label="Menü öffnen"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Tenant pill */}
      <div className="order-3 w-full min-w-0 flex items-center gap-2 md:order-none md:w-auto md:flex-1 md:flex-initial">
        <div className="flex w-full min-w-0 items-center gap-2 rounded-lg border border-border bg-card/60 px-2.5 py-1.5 md:w-auto">
          <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-sm font-medium truncate max-w-full sm:max-w-[320px] lg:max-w-[420px]">
            {tenantLabel}
          </span>
          {tenants.length > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="tenant-switcher"
                className="inline-flex shrink-0 items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                Mandant wechseln <ChevronDown className="w-3 h-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuLabel>Mandant wählen</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {tenants.map((t) => (
                  <DropdownMenuItem
                    key={t.id}
                    data-testid={`tenant-switcher-item-${t.slug}`}
                    onClick={() => handleSwitch(t.slug)}
                    className={t.id === tenant?.id ? "font-semibold" : ""}
                  >
                    {t.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="order-2 ml-auto flex shrink-0 items-center gap-2">
        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger
            data-testid="user-menu"
            className="inline-flex shrink-0 items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-muted transition-colors btn-tactile"
          >
            <div
              className="w-8 h-8 rounded-xl border border-brand/30 bg-brand/10 flex items-center justify-center text-brand text-[11px] font-semibold"
              data-testid="topbar-user-avatar"
            >
              {initials}
            </div>
            <div className="hidden md:flex flex-col items-start leading-tight pr-1">
              <span className="text-xs font-semibold">{user?.full_name}</span>
              <span className="text-[10px] text-muted-foreground">
                {ROLE_LABELS[role] || role}
              </span>
            </div>
            <ChevronDown className="w-3 h-3 text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="text-sm font-semibold">{user?.full_name}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isSuperAdmin && (
              <DropdownMenuItem onClick={() => navigate("/admin/tenants")}>
                <Shield className="w-4 h-4 mr-2" /> Mandanten
              </DropdownMenuItem>
            )}
            {isSuperAdmin && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={() => navigate("/einstellungen")}>
              <UserIcon className="w-4 h-4 mr-2" /> Einstellungen
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="logout-button" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" /> Abmelden
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
